// src/components/layouts/MinimalistLayout.tsx
'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { getMenuOptions } from '@/components/menu';

export default function MinimalistLayout({ children, title, userName, userRole, userAvatar, userInitials, handleLogout, config, API_URL }: any) {
  const pathname = usePathname();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const isActive = (path: string) => pathname === path;
  const menuConfig = getMenuOptions(userRole);

  const logoUrl = config?.logo_url ? (config.logo_url.startsWith('http') ? config.logo_url : `${API_URL}${config.logo_url}`) : null;
  const companyName = config?.company_name || "Cash Money";

  return (
    <div className="min-h-screen bg-[#F4F5F7] text-gray-800 font-sans flex">
      
      {/* OVERLAY PARA MÓVILES */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR MINIMALISTA */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center px-6 border-b border-gray-100">
          
          <div className="flex items-center overflow-hidden gap-3">
             {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
             ) : (
                <div className="w-8 h-8 rounded bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  {companyName.charAt(0)}
                </div>
             )}
             <span className="font-bold text-lg tracking-tight truncate max-w-[130px]" title={companyName}>{companyName}</span>
          </div>
          
        </div>
        
        <nav className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-4rem)] scrollbar-thin">
          {menuConfig.map((group, index) => (
            <div key={index} className="space-y-1">
              <div className="px-3 mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{group.section}</div>
              {group.items.map((item: any, i: number) => (
                <Link key={i} href={item.path} onClick={() => setIsSidebarOpen(false)} className={`flex items-center space-x-3 px-3 py-2.5 text-sm rounded-md transition-colors ${isActive(item.path) ? 'bg-blue-50 text-blue-700 font-bold border-r-4 border-blue-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                  <span className="text-lg opacity-80">{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* CONTENIDO PRINCIPAL */}
      <div className="flex-1 flex flex-col w-full overflow-hidden">
        {/* HEADER RESPONSIVO */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 z-30">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-md">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <h1 className="text-xl font-bold text-gray-800">{title}</h1>
          </div>
          
          <div className="relative">
            <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center gap-3 focus:outline-none cursor-pointer">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-gray-700">{userName}</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{userRole}</p>
              </div>
              {userAvatar ? (
                 <img src={userAvatar} alt={userName} className="w-9 h-9 rounded-full object-cover border border-gray-200" />
              ) : (
                 <div className="w-9 h-9 rounded-md border border-gray-300 bg-gray-50 flex items-center justify-center font-bold text-blue-600 text-sm">
                   {userInitials}
                 </div>
              )}
            </button>
            {isProfileMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-lg shadow-lg py-1 z-50">
                <button onClick={handleLogout} className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-gray-50 font-medium">Cerrar Sesión</button>
              </div>
            )}
          </div>
        </header>

        {/* ÁREA DE TRABAJO */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
}